/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  Button,
  Col,
  Row,
  Table,
  Input,
  Label,
  FormGroup,
  Card,
  CardBody,
  Collapse,
} from 'reactstrap';
import ReactSelect from 'react-select';

import { CompanyId } from '../../constants';
import LocalstorageService from '../../helpers/localstorage-service';
import { setLoadingOverlay } from '../../store/slices/loadingOverlaySlice';
import reportService from '../../services/report';
import { exportToExcel, exportToPDF } from '../../helpers/exportUtils';
import NoRecords from '../../components/Table/NoRecords';

import './WithdrawalReport.css';

const WithdrawalReport = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const [setPageTitle, setCrumbItem] = useOutletContext();
  const isRTL = i18n.language === 'he';

  const companyId = LocalstorageService.getItem(CompanyId);
  const currentYear = new Date().getFullYear();

  // Filter States
  const [fromYear, setFromYear] = useState(currentYear);
  const [toYear, setToYear] = useState(currentYear);
  const [fromMonth, setFromMonth] = useState(1);
  const [toMonth, setToMonth] = useState(12);
  const [fromDay, setFromDay] = useState(1);
  const [toDay, setToDay] = useState(31);
  const [allMonths, setAllMonths] = useState(true);
  const [allDays, setAllDays] = useState(true);
  const [factor, setFactor] = useState('1.000');
  const [displayMode, setDisplayMode] = useState('month');

  // Terminal Selection - כמו ב-ChargingTerminal.js
  const [terminalList, setTerminalList] = useState([]);       // רשימת כל הנתונים מה-API
  const [locOptList, setLocOptList] = useState([]);           // אופציות למקומות
  const [termOptList, setTermOptList] = useState([]);         // אופציות למסופים
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [selectedTerminalNumber, setSelectedTerminalNumber] = useState(null);
  const [selectedTerminals, setSelectedTerminals] = useState([]); // מסופים שנבחרו לדוח

  // Report Data
  const [reportData, setReportData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isResultsOpen, setIsResultsOpen] = useState(true);

  // Generate year options (last 10 years)
  const yearOptions = useMemo(() => {
    const years = [];
    for (let y = currentYear; y >= currentYear - 10; y--) {
      years.push({ value: y, label: y.toString() });
    }
    return years;
  }, [currentYear]);

  // Generate month options
  const monthOptions = useMemo(() => [
    { value: 1, label: t('WITHDRAWAL_REPORT.MONTHS.JAN') },
    { value: 2, label: t('WITHDRAWAL_REPORT.MONTHS.FEB') },
    { value: 3, label: t('WITHDRAWAL_REPORT.MONTHS.MAR') },
    { value: 4, label: t('WITHDRAWAL_REPORT.MONTHS.APR') },
    { value: 5, label: t('WITHDRAWAL_REPORT.MONTHS.MAY') },
    { value: 6, label: t('WITHDRAWAL_REPORT.MONTHS.JUN') },
    { value: 7, label: t('WITHDRAWAL_REPORT.MONTHS.JUL') },
    { value: 8, label: t('WITHDRAWAL_REPORT.MONTHS.AUG') },
    { value: 9, label: t('WITHDRAWAL_REPORT.MONTHS.SEP') },
    { value: 10, label: t('WITHDRAWAL_REPORT.MONTHS.OCT') },
    { value: 11, label: t('WITHDRAWAL_REPORT.MONTHS.NOV') },
    { value: 12, label: t('WITHDRAWAL_REPORT.MONTHS.DEC') },
  ], [t]);

  // Generate day options
  const dayOptions = useMemo(() => {
    const days = [];
    for (let d = 1; d <= 31; d++) {
      days.push({ value: d, label: d.toString() });
    }
    return days;
  }, []);

  // Filter available "to" options based on "from" selection
  const filteredToYearOptions = useMemo(() => {
    return yearOptions.filter(y => y.value >= fromYear);
  }, [yearOptions, fromYear]);

  const filteredToMonthOptions = useMemo(() => {
    if (fromYear === toYear) {
      return monthOptions.filter(m => m.value >= fromMonth);
    }
    return monthOptions;
  }, [monthOptions, fromYear, toYear, fromMonth]);

  const filteredToDayOptions = useMemo(() => {
    if (fromYear === toYear && fromMonth === toMonth) {
      return dayOptions.filter(d => d.value >= fromDay);
    }
    return dayOptions;
  }, [dayOptions, fromYear, toYear, fromMonth, toMonth, fromDay]);

  useEffect(() => {
    setPageTitle(t('WITHDRAWAL_REPORT.TITLE'));
    setCrumbItem([
      { title: t('APPLICATION_TITLE'), link: '#' },
      { title: t('REPORT.TITLE'), link: '#' },
      { title: t('WITHDRAWAL_REPORT.TITLE'), link: '#' },
    ]);
    loadTerminals();
  }, []);

  // Auto-adjust "to" values when "from" values change
  useEffect(() => {
    if (toYear < fromYear) {
      setToYear(fromYear);
    }
  }, [fromYear, toYear]);

  useEffect(() => {
    if (fromYear === toYear && toMonth < fromMonth) {
      setToMonth(fromMonth);
    }
  }, [fromMonth, fromYear, toYear, toMonth]);

  useEffect(() => {
    if (fromYear === toYear && fromMonth === toMonth && toDay < fromDay) {
      setToDay(fromDay);
    }
  }, [fromDay, fromYear, toYear, fromMonth, toMonth, toDay]);

  // טעינת מסופים - כמו getLocationsFn ב-ChargingTerminal.js
  const loadTerminals = async () => {
    dispatch(setLoadingOverlay(true));
    try {
      const response = await reportService.getTerminalsForReport({ companyId });
      const { status, dataList } = response.data;
      
      if (status === 200 && dataList && dataList.length) {
        // שמירת הרשימה המלאה
        setTerminalList(dataList);
        
        // יצירת אופציות למקומות (כמו ב-ChargingTerminal)
        const locOptions = dataList.map((item) => ({
          id: item.locationId,
          label: item.locationName,
          value: item.locationId,
        }));
        
        // יצירת אופציות למסופים (כמו ב-ChargingTerminal)
        const termOptions = dataList.map((item) => ({
          id: item.terminalNumber,
          label: item.terminalNumber,
          value: item.terminalNumber,
        }));
        
        setLocOptList(locOptions);
        setTermOptList(termOptions);
      }
    } catch (error) {
      console.error('Error loading terminals:', error);
      toast.error(t('WITHDRAWAL_REPORT.ERROR_LOADING_TERMINALS'));
    } finally {
      dispatch(setLoadingOverlay(false));
    }
  };

  // כשבוחרים מקום - מעדכנים את המסוף (כמו ב-ChargingTerminal)
  const handleLocationChange = (option) => {
    setSelectedLocationId(option ? option.value : null);
    
    if (option) {
      // מוצאים את המסוף המתאים
      const locObj = terminalList.find((item) => item.locationId === option.value);
      if (locObj) {
        setSelectedTerminalNumber(locObj.terminalNumber);
      }
    } else {
      setSelectedTerminalNumber(null);
    }
  };

  // כשבוחרים מסוף - מעדכנים את המקום (כמו ב-ChargingTerminal)
  const handleTerminalChange = (option) => {
    setSelectedTerminalNumber(option ? option.value : null);
    
    if (option) {
      // מוצאים את המקום המתאים
      const termObj = terminalList.find((item) => item.terminalNumber === option.value);
      if (termObj) {
        setSelectedLocationId(termObj.locationId);
      }
    } else {
      setSelectedLocationId(null);
    }
  };

  // הוספת מסוף לרשימה
  const addTerminal = () => {
    if (!selectedTerminalNumber) return;

    const exists = selectedTerminals.find(t => t.terminalNumber === selectedTerminalNumber);
    if (exists) {
      toast.warning(t('WITHDRAWAL_REPORT.TERMINAL_ALREADY_ADDED'));
      return;
    }

    const termObj = terminalList.find(t => t.terminalNumber === selectedTerminalNumber);
    if (termObj) {
      setSelectedTerminals([...selectedTerminals, termObj]);
    }
    
    // איפוס הבחירה
    setSelectedLocationId(null);
    setSelectedTerminalNumber(null);
  };

  // הסרת מסוף מהרשימה
  const removeTerminal = (terminalNumber) => {
    setSelectedTerminals(selectedTerminals.filter(t => t.terminalNumber !== terminalNumber));
  };

  const handleFactorChange = (e) => {
    let value = e.target.value;
    
    if (value === '') {
      setFactor('');
      return;
    }

    const numVal = parseFloat(value);
    if (!isNaN(numVal) && numVal >= 0 && numVal <= 1) {
      setFactor(value);
    }
  };

  const handleFactorBlur = () => {
    if (factor === '' || isNaN(parseFloat(factor))) {
      setFactor('1.000');
    } else {
      const numVal = parseFloat(factor);
      setFactor(Math.min(1, Math.max(0, numVal)).toFixed(3));
    }
  };

  const generateReport = async () => {
    // בדיקה אם לא נבחרו מסופים - הצגת אישור
    if (selectedTerminals.length === 0) {
      const confirmed = window.confirm(t('WITHDRAWAL_REPORT.CONFIRM_ALL_TERMINALS'));
      if (!confirmed) {
        return;
      }
    }

    setIsLoading(true);
    dispatch(setLoadingOverlay(true));

    try {
      const payload = {
        companyId,
        fromYear,
        toYear,
        fromMonth: allMonths ? 1 : fromMonth,
        toMonth: allMonths ? 12 : toMonth,
        fromDay: allDays ? 1 : fromDay,
        toDay: allDays ? 31 : toDay,
        allMonths,
        allDays,
        factor: parseFloat(factor) || 1,
        displayMode,
        terminalNumbers: selectedTerminals.map(t => t.terminalNumber),
      };

      const response = await reportService.getWithdrawalCountReport(payload);
      const { status, data, summary: summaryData, message } = response.data;

      if (status === 200) {
        setReportData(data || []);
        setSummary(summaryData || null);
        setIsResultsOpen(true);
        
        if (!data || data.length === 0) {
          toast.info(t('WITHDRAWAL_REPORT.NO_DATA'));
        }
      } else {
        toast.error(message || t('WITHDRAWAL_REPORT.ERROR'));
      }
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error(t('WITHDRAWAL_REPORT.ERROR'));
    } finally {
      setIsLoading(false);
      dispatch(setLoadingOverlay(false));
    }
  };

  const resetFilters = () => {
    setFromYear(currentYear);
    setToYear(currentYear);
    setFromMonth(1);
    setToMonth(12);
    setFromDay(1);
    setToDay(31);
    setAllMonths(true);
    setAllDays(true);
    setFactor('1.000');
    setDisplayMode('month');
    setSelectedTerminals([]);
    setSelectedLocationId(null);
    setSelectedTerminalNumber(null);
    setReportData([]);
    setSummary(null);
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    return new Intl.NumberFormat('he-IL', { maximumFractionDigits: 0 }).format(num);
  };

  const getMonthName = (monthNum) => {
    const month = monthOptions.find(m => m.value === monthNum);
    return month ? month.label : monthNum;
  };

  // Column definitions for export
  const withdrawalReportColumns = [
    { key: 'rowNum', header: '#' },
    { key: 'period', header: t('WITHDRAWAL_REPORT.TABLE.PERIOD') },
    { key: 'terminalNumber', header: t('WITHDRAWAL_REPORT.TABLE.TERMINAL') },
    { key: 'locationName', header: t('WITHDRAWAL_REPORT.TABLE.LOCATION') },
    { key: 'withdrawalCount', header: t('WITHDRAWAL_REPORT.TABLE.WITHDRAWAL_COUNT') },
  ];

  // Export to Excel using exportUtils
  const handleExportExcel = () => {
    if (reportData.length === 0) {
      toast.warning(t('REPORT.NO_DATA_TO_EXPORT') || 'אין נתונים לייצוא');
      return;
    }

    const dataWithRowNum = reportData.map((item, index) => ({
      ...item,
      rowNum: index + 1,
    }));

    const filename = `WithdrawalReport_${new Date().toLocaleDateString('en-GB').replace(/\//g, '-')}`;
    const title = t('WITHDRAWAL_REPORT.TITLE');
    
    const success = exportToExcel(dataWithRowNum, withdrawalReportColumns, filename, title);
    if (success) {
      toast.success(t('REPORT.EXPORT_SUCCESS') || 'הקובץ יורד בהצלחה');
    } else {
      toast.error(t('REPORT.EXPORT_FAILED') || 'שגיאה בייצוא');
    }
  };

  // Export to PDF using exportUtils
  const handleExportPDF = () => {
    if (reportData.length === 0) {
      toast.warning(t('REPORT.NO_DATA_TO_EXPORT') || 'אין נתונים לייצוא');
      return;
    }

    const dataWithRowNum = reportData.map((item, index) => ({
      ...item,
      rowNum: index + 1,
    }));

    const filename = `WithdrawalReport_${new Date().toLocaleDateString('en-GB').replace(/\//g, '-')}`;
    const title = t('WITHDRAWAL_REPORT.TITLE');
    
    const success = exportToPDF(dataWithRowNum, withdrawalReportColumns, filename, title);
    if (success) {
      toast.success(t('REPORT.EXPORT_SUCCESS') || 'נפתח חלון הדפסה');
    } else {
      toast.error(t('REPORT.EXPORT_FAILED') || 'שגיאה בייצוא');
    }
  };

  // Print - same as PDF export
  const handlePrint = () => {
    handleExportPDF();
  };

  // Group data by year/month for hierarchical display
  const groupedData = useMemo(() => {
    if (!reportData.length) return {};

    const grouped = {};
    reportData.forEach(item => {
      const yearKey = item.reportYear;
      const monthKey = `${item.reportYear}-${item.reportMonth}`;

      if (!grouped[yearKey]) {
        grouped[yearKey] = { total: 0, months: {} };
      }

      if (displayMode !== 'year') {
        if (!grouped[yearKey].months[monthKey]) {
          grouped[yearKey].months[monthKey] = { total: 0, items: [] };
        }
        grouped[yearKey].months[monthKey].items.push(item);
        grouped[yearKey].months[monthKey].total += item.withdrawalCount;
      }

      grouped[yearKey].total += item.withdrawalCount;
    });

    return grouped;
  }, [reportData, displayMode]);

  // Custom styles for ReactSelect
  const selectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: '38px',
      cursor: 'pointer',
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999,
      maxHeight: '300px',
    }),
    menuList: (provided) => ({
      ...provided,
      maxHeight: '300px',
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 9999,
    }),
  };

  return (
    <div className={`withdrawal-report ${isRTL ? 'rtl' : 'ltr'}`}>
      {/* Filters Section */}
      <Card className="mb-3">
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">🔍 {t('WITHDRAWAL_REPORT.FILTERS')}</h5>
          </div>

          {/* Year Range */}
          <Row className="mb-3">
            <Col md={6}>
              <Label>{t('WITHDRAWAL_REPORT.YEAR_RANGE')}</Label>
              <div className="d-flex align-items-center gap-2">
                <ReactSelect
                  value={yearOptions.find(y => y.value === fromYear)}
                  onChange={(opt) => setFromYear(opt.value)}
                  options={yearOptions}
                  styles={selectStyles}
                  className="flex-grow-1"
                  isRtl={isRTL}
                />
                <span className="text-muted">{t('WITHDRAWAL_REPORT.TO')}</span>
                <ReactSelect
                  value={filteredToYearOptions.find(y => y.value === toYear)}
                  onChange={(opt) => setToYear(opt.value)}
                  options={filteredToYearOptions}
                  styles={selectStyles}
                  className="flex-grow-1"
                  isRtl={isRTL}
                />
              </div>
            </Col>
            
            {/* Factor */}
            <Col md={6}>
              <Label>{t('WITHDRAWAL_REPORT.FACTOR')}</Label>
              <div className="d-flex align-items-center gap-2">
                <Input
                  type="number"
                  value={factor}
                  onChange={handleFactorChange}
                  onBlur={handleFactorBlur}
                  min="0"
                  max="1"
                  step="0.001"
                  className="factor-input"
                />
                <span className="text-muted factor-hint">(0.000 - 1.000)</span>
              </div>
            </Col>
          </Row>

          {/* Month Range */}
          <Row className="mb-3">
            <Col md={6}>
              <FormGroup check className="mb-2">
                <Input
                  type="checkbox"
                  id="allMonths"
                  checked={allMonths}
                  onChange={(e) => setAllMonths(e.target.checked)}
                />
                <Label check for="allMonths">{t('WITHDRAWAL_REPORT.ALL_MONTHS')}</Label>
              </FormGroup>
              {!allMonths && (
                <div className="d-flex align-items-center gap-2">
                  <ReactSelect
                    value={monthOptions.find(m => m.value === fromMonth)}
                    onChange={(opt) => setFromMonth(opt.value)}
                    options={monthOptions}
                    styles={selectStyles}
                    className="flex-grow-1"
                    isRtl={isRTL}
                  />
                  <span className="text-muted">{t('WITHDRAWAL_REPORT.TO')}</span>
                  <ReactSelect
                    value={filteredToMonthOptions.find(m => m.value === toMonth)}
                    onChange={(opt) => setToMonth(opt.value)}
                    options={filteredToMonthOptions}
                    styles={selectStyles}
                    className="flex-grow-1"
                    isRtl={isRTL}
                  />
                </div>
              )}
            </Col>

            {/* Day Range */}
            <Col md={6}>
              <FormGroup check className="mb-2">
                <Input
                  type="checkbox"
                  id="allDays"
                  checked={allDays}
                  onChange={(e) => setAllDays(e.target.checked)}
                />
                <Label check for="allDays">{t('WITHDRAWAL_REPORT.ALL_DAYS')}</Label>
              </FormGroup>
              {!allDays && (
                <div className="d-flex align-items-center gap-2">
                  <ReactSelect
                    value={dayOptions.find(d => d.value === fromDay)}
                    onChange={(opt) => setFromDay(opt.value)}
                    options={dayOptions}
                    styles={selectStyles}
                    className="flex-grow-1"
                    isRtl={isRTL}
                  />
                  <span className="text-muted">{t('WITHDRAWAL_REPORT.TO')}</span>
                  <ReactSelect
                    value={filteredToDayOptions.find(d => d.value === toDay)}
                    onChange={(opt) => setToDay(opt.value)}
                    options={filteredToDayOptions}
                    styles={selectStyles}
                    className="flex-grow-1"
                    isRtl={isRTL}
                  />
                </div>
              )}
            </Col>
          </Row>

          {/* Display Mode */}
          <Row className="mb-3">
            <Col md={12}>
              <Label>{t('WITHDRAWAL_REPORT.DISPLAY_BY')}</Label>
              <div className="d-flex gap-3">
                <FormGroup check inline>
                  <Input
                    type="radio"
                    id="modeYear"
                    name="displayMode"
                    value="year"
                    checked={displayMode === 'year'}
                    onChange={() => setDisplayMode('year')}
                  />
                  <Label check for="modeYear">📅 {t('WITHDRAWAL_REPORT.BY_YEAR')}</Label>
                </FormGroup>
                <FormGroup check inline>
                  <Input
                    type="radio"
                    id="modeMonth"
                    name="displayMode"
                    value="month"
                    checked={displayMode === 'month'}
                    onChange={() => setDisplayMode('month')}
                  />
                  <Label check for="modeMonth">🗓️ {t('WITHDRAWAL_REPORT.BY_MONTH')}</Label>
                </FormGroup>
                <FormGroup check inline>
                  <Input
                    type="radio"
                    id="modeDay"
                    name="displayMode"
                    value="day"
                    checked={displayMode === 'day'}
                    onChange={() => setDisplayMode('day')}
                  />
                  <Label check for="modeDay">📆 {t('WITHDRAWAL_REPORT.BY_DAY')}</Label>
                </FormGroup>
              </div>
            </Col>
          </Row>

          {/* Terminal Selection - כמו ב-ChargingTerminal.js */}
          <Row className="mb-3">
            <Col md={12}>
              <Label>🏧 {t('WITHDRAWAL_REPORT.SELECT_TERMINALS')}</Label>
              <div className="terminal-selector">
                <div className="d-flex gap-2 flex-wrap align-items-end">
                  {/* בחירה לפי מקום */}
                  <div className="flex-grow-1" style={{ minWidth: '200px' }}>
                    <small className="text-muted">{t('WITHDRAWAL_REPORT.BY_NAME')}</small>
                    <ReactSelect
                      value={locOptList.find(opt => opt.value === selectedLocationId) || null}
                      onChange={handleLocationChange}
                      options={locOptList}
                      styles={selectStyles}
                      placeholder={`-- ${t('SELECT')} --`}
                      isClearable
                      isRtl={isRTL}
                      noOptionsMessage={() => t('RSEL_NO_OPT')}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>
                  
                  {/* בחירה לפי מספר מסוף */}
                  <div className="flex-grow-1" style={{ minWidth: '150px' }}>
                    <small className="text-muted">{t('WITHDRAWAL_REPORT.BY_NUMBER')}</small>
                    <ReactSelect
                      value={termOptList.find(opt => opt.value === selectedTerminalNumber) || null}
                      onChange={handleTerminalChange}
                      options={termOptList}
                      styles={selectStyles}
                      placeholder={`-- ${t('SELECT')} --`}
                      isClearable
                      isRtl={isRTL}
                      noOptionsMessage={() => t('RSEL_NO_OPT')}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                    />
                  </div>
                  
                  <Button
                    color="primary"
                    onClick={addTerminal}
                    disabled={!selectedTerminalNumber}
                    className="add-terminal-btn"
                  >
                    ➕ {t('ADD_TITLE')}
                  </Button>
                </div>

                {/* רשימת מסופים שנבחרו */}
                {selectedTerminals.length > 0 && (
                  <div className="selected-terminals mt-3">
                    {selectedTerminals.map((terminal) => (
                      <span key={terminal.terminalNumber} className="terminal-tag">
                        {terminal.terminalNumber} - {terminal.locationName}
                        <button
                          className="remove-btn"
                          onClick={() => removeTerminal(terminal.terminalNumber)}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </Col>
          </Row>

          {/* Action Buttons */}
          <Row>
            <Col className="d-flex justify-content-end gap-2">
              <Button color="secondary" outline onClick={resetFilters}>
                🔄 {t('WITHDRAWAL_REPORT.RESET')}
              </Button>
              <Button color="primary" onClick={generateReport} disabled={isLoading}>
                📊 {t('WITHDRAWAL_REPORT.GENERATE')}
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      {/* Results Section */}
      {reportData.length > 0 && (
        <Card>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0" onClick={() => setIsResultsOpen(!isResultsOpen)} style={{ cursor: 'pointer' }}>
                {isResultsOpen ? '▼' : '▶'} {t('WITHDRAWAL_REPORT.RESULTS')}
              </h5>
              <div className="d-flex align-items-center gap-2">
                {summary && (
                  <div className="summary-badge">
                    {t('WITHDRAWAL_REPORT.TOTAL')}: <strong>{formatNumber(summary.totalWithdrawals)}</strong>
                  </div>
                )}
                <Button color="success" size="sm" onClick={handleExportExcel}>
                  📥 {t('WITHDRAWAL_REPORT.EXPORT_EXCEL')}
                </Button>
                <Button color="danger" size="sm" onClick={handleExportPDF}>
                  📄 {t('WITHDRAWAL_REPORT.EXPORT_PDF')}
                </Button>
                <Button color="info" size="sm" onClick={handlePrint}>
                  🖨️ {t('WITHDRAWAL_REPORT.PRINT')}
                </Button>
              </div>
            </div>

            <Collapse isOpen={isResultsOpen}>
              <div className="table-responsive">
                <Table bordered hover className="report-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>{t('WITHDRAWAL_REPORT.TABLE.PERIOD')}</th>
                      <th>{t('WITHDRAWAL_REPORT.TABLE.TERMINAL')}</th>
                      <th>{t('WITHDRAWAL_REPORT.TABLE.LOCATION')}</th>
                      <th>{t('WITHDRAWAL_REPORT.TABLE.WITHDRAWAL_COUNT')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.map((item, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td>{item.period || `${item.reportYear}/${item.reportMonth || ''}`}</td>
                        <td>{item.terminalNumber}</td>
                        <td>{item.locationName}</td>
                        <td className="text-center">{formatNumber(item.withdrawalCount)}</td>
                      </tr>
                    ))}
                  </tbody>
                  {summary && (
                    <tfoot>
                      <tr className="summary-row">
                        <td colSpan={4} className="text-end">
                          <strong>{t('WITHDRAWAL_REPORT.TOTAL')}:</strong>
                        </td>
                        <td className="text-center">
                          <strong>{formatNumber(summary.totalWithdrawals)}</strong>
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </Table>
              </div>
            </Collapse>
          </CardBody>
        </Card>
      )}

      {/* No Data Message */}
      {!isLoading && reportData.length === 0 && (
        <Card>
          <CardBody>
            <NoRecords />
          </CardBody>
        </Card>
      )}
    </div>
  );
};

export default WithdrawalReport;
